import { reelExemptElementIds } from '@src/services/reel/reelExemptElementIds';
import { reelSectionContents } from '@src/services/reel/reelSectionContents';
import { SceneService } from '@src/services/SceneService';
import { findIntroCardEndTime } from '@src/utility/extraElementData';

import { TranscriptTrack } from '../../models/TranscriptTrack';
import { RestoreTranscriptFromOriginalCommand } from '../transcript/RestoreTranscriptFromOriginalCommand';

import type { VideoTranscriptElement } from '../../models/elements/VideoTranscriptElement';
import type { VideoEditor } from '../../VideoEditor';
import type { ReelSection } from '@src/services/reel/deriveReelSections';

/**
 * A Section as this layout reads one: an id, and the run of transcript elements
 * it owns. Narrower than {@link ReelSection} so a Section that does not exist
 * yet — one an insert is about to create — can be handed in as a target
 * position without inventing times for it that the layout is about to assign.
 */
export type ReelSectionOrderEntry = Pick<ReelSection, 'id' | 'elementIds'>;

/**
 * PLAT-11056 — re-lays the reel out in a new Section order.
 *
 * Shared by {@link MoveReelSectionCommand}, {@link MoveReelChapterCommand} and
 * {@link InsertReelSectionsCommand}: moving a Chapter is moving its run of
 * Sections (DE6), and an insert is the same layout over an order with new
 * Sections spliced into it — so all three differ only in the permutation they
 * hand in, and the layout maths is one body rather than three.
 *
 * Reordering is a **timeline** operation followed by a re-projection (DE6),
 * never a transcript cut-and-paste: the timeline is the clip, and the
 * transcript is a projection of it (DE3).
 *
 * 0. Capture every scene as per-element chunks, in element-local coordinates,
 *    so each chunk can follow the element it covers through the permutation.
 * 1. Capture the elements each Section owns — those whose start falls inside
 *    its span (DE7) — as offsets *relative to that Section's own start*.
 * 2. Rebuild the transcript track from the permuted Section runs.
 * 3. Re-lay it out contiguously from where the passage is allowed to start —
 *    after the intro card, if there is one — the same pass the reel crop
 *    performs.
 * 4. Carry the owned elements to their Section's new start, then re-project.
 * 5. Re-assemble the scenes from those chunks, merging the ones that landed
 *    adjacent again.
 *
 * Mutates the timeline in place; the caller is a command, whose snapshot taken
 * before this ran is what an undo restores. The caller must then run
 * `recalculateTimeline()`, which reconciles the captions against the
 * re-projected transcript, re-derives the pinned cards and graphics against the
 * new content end, and writes the duration.
 *
 * @param sections Every Section that exists now, in the order they are in now
 * @param order The order to play in — those Sections permuted, and for an
 *   insert the arriving ones spliced in at their boundary
 * @param exemptElementIds Reel-level elements that must not be carried (DE7),
 *   on top of the ones `reelExemptElementIds` derives from the timeline
 * @param addedElements Elements not yet on the track, which `order` refers to
 * @returns Whether the re-projection succeeded, and where the passage now ends
 *   — lead-in included, so it is a timeline time and not a length
 */
export function applyReelSectionOrder(
  editor: VideoEditor,
  track: TranscriptTrack,
  sections: readonly ReelSection[],
  order: readonly ReelSectionOrderEntry[],
  exemptElementIds?: ReadonlySet<string>,
  addedElements?: readonly VideoTranscriptElement[],
): { restored: boolean; duration: number } {
  const timeline = editor.timeline;
  const sceneService = new SceneService();
  const extraData = editor.getTrackManager().extraElementDataRaw ?? {};

  // The reel-level elements, derived from the timeline rather than taken on
  // trust from the caller — no production caller passed the parameter, so the
  // intro card, the end card and the logo were all being treated as Section
  // contents and carried around (or, on a removal, deleted).
  const exempt = reelExemptElementIds(
    timeline.tracks,
    extraData,
    exemptElementIds,
  );

  // Step 0 — the scenes, before a single `element.time` moves. A scene anchor
  // holds an element id plus an offset *inside* that element, so re-anchoring
  // every scene onto the elements it currently covers is what lets a scene
  // survive an arbitrary permutation: each chunk travels with its own element
  // and is merged back with its neighbours afterwards. Without this the anchors
  // still resolve — to the elements' new positions — so a scene reaching across
  // a Section boundary comes out inverted or stretched across the whole reel.
  // Same capture/restore pair `ChangeElementPlacementsCommand` runs around a
  // transcript drag, for the same reason.
  //
  // Only elements the new order keeps: a chunk anchored to one this call is
  // about to drop (a removal) would resolve against a timeline that no longer
  // has it, and `AnchorResolver` throws on that.
  const retainedElementIds = new Set(
    order.flatMap(section => section.elementIds),
  );
  const sceneSnapshots = sceneService.captureScenesForElements(
    timeline,
    track.elements.filter(element => retainedElementIds.has(element.id)),
  );

  // Step 1 — what each Section owns, and where inside it. Captured for *every*
  // Section, because a re-layout moves them all, not only the ones permuted.
  const ownedBySection = sections.map(section =>
    reelSectionContents(timeline, section, {
      excludeElementIds: exempt,
    }).map(element => ({
      element,
      offsetInSection: element.time - section.startTime,
    })),
  );

  // The elements `order` can name: what the track holds, plus the portions an
  // insert has just built and not yet put anywhere (they arrive through the
  // rebuild below rather than being spliced into the old track first).
  const elementsById = new Map(track.elements.map(el => [el.id, el]));
  for (const element of addedElements ?? []) {
    elementsById.set(element.id, element);
  }

  // Step 2 — the element order the permuted Sections imply.
  const permutedElements: VideoTranscriptElement[] = [];
  for (const section of order) {
    for (const elementId of section.elementIds) {
      const element = elementsById.get(elementId);
      if (element) permutedElements.push(element);
    }
  }

  // Step 3 — contiguous from where the passage may start, recording where each
  // Section now begins so its owned elements can follow it.
  //
  // Not from 0: an intro card occupies the head of the timeline and the insert
  // rippled the passage clear of it
  const newSectionStart = new Map<string, number>();
  let offset = findIntroCardEndTime(timeline.tracks, extraData);
  for (const section of order) {
    newSectionStart.set(section.id, offset);
    for (const elementId of section.elementIds) {
      const element = elementsById.get(elementId);
      if (!element) continue;
      element.time = offset;
      offset += element.durationSeconds;
    }
  }

  const reordered = new TranscriptTrack({
    id: track.id,
    type: track.type,
    name: track.name,
    locked: track.locked,
    visible: track.visible,
    transcriptSegmentElements: permutedElements,
  });
  timeline.replaceTrack(track.id, reordered);
  editor.setTranscriptTrack(reordered.id);

  // Step 4 — carry each Section's own elements to its new start, keeping the
  // offset they had inside it. Absolute deltas would be wrong: the re-layout
  // can move a Section's neighbours by a different amount.
  sections.forEach((section, index) => {
    const start = newSectionStart.get(section.id);
    if (start == null) return;
    for (const { element, offsetInSection } of ownedBySection[index]) {
      element.time = start + offsetInSection;
    }
  });

  const restored = new RestoreTranscriptFromOriginalCommand(editor).execute();

  // Step 5 — the scenes, re-assembled against the re-projected transcript. The
  // rebuild above replaced every element object, but the chunks are anchored by
  // id, so they resolve onto the new ones. Chunks that ended up adjacent merge
  // back into the scene they came from; ones the permutation pulled apart stay
  // split, each keeping the layout it had — the same outcome a drag produces.
  sceneService.restoreScenesFromSnapshots(timeline, sceneSnapshots);

  return { restored, duration: offset };
}
